import {FC, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {File} from 'react-pdf/dist/cjs/shared/types';
import ZipFileLarge from 'assets/svg/ZipFileLarge';
import clsx from 'clsx';
import Notification from 'components/Notification';
import PDFReader from 'components/PdfReader';
import SectionHeader from 'components/SectionHeader';
import {useBackend} from 'state/backend';
import LoggedInHeader from '../components/LoggedInHeader';
import PortalPageLayout from '../components/PortalPageLayout';
import {useGetClientInboxItemPdf} from '../services';
import AcceptOrderPanel from './Panels/AcceptOrderPanel';
import BizpayPanel from './Panels/BizpayPanel';
import InfoPanel from './Panels/InfoPanel';
import LoginMakeLeaps from './Panels/LoginMakeLeaps';
import PaymentPanel from './Panels/PaymentPanel';
import ReceiveDocumentPanel from './Panels/ReceiveDocumentPanel';
import {ClientInboxItemBackend} from './types';

const ClientInboxItem: FC = () => {
    const {
        data: {
            bizpayAffiliateUrl,
            document,
            email,
            moneyRequest,
            payWithBizpay,
            portalMid,
            sessionExpiration,
        },
    } = useBackend<ClientInboxItemBackend>();
    const {t} = useTranslation('Portal');
    const isZipFile = document.filename.slice(-3) === 'zip';
    const {mutateAsync} = useGetClientInboxItemPdf();
    const [pdfResponse, setPdfResponse] = useState<File>(null);

    const fetchPdf = useCallback(() => {
        if (!isZipFile) {
            mutateAsync({
                documentMid: document.mid,
                portalMid,
            }).then((res) => {
                if (res) {
                    const blob = new Blob([res.data], {
                        type: 'application/pdf',
                    });
                    const blobUrl = URL.createObjectURL(blob);
                    setPdfResponse(blobUrl);
                }
            });
        }
    }, [document.mid, isZipFile, mutateAsync, portalMid]);

    useEffect(() => {
        fetchPdf();
    }, [fetchPdf]);

    return (
        <PortalPageLayout
            className="flex flex-1 flex-col mx-4"
            isResponsive={false}
        >
            <LoggedInHeader
                backLabel={t('clientInboxItem.returnToClientInbox')}
                backUrl={`/portal/${portalMid}/client-inbox`}
                email={email}
                sessionExpiration={sessionExpiration}
            />
            <SectionHeader level={1} title={document.displayName} />
            {document.isCancelled && (
                <Notification className="mt-4" icon="warning" type="alert">
                    {t('clientInboxItem.cancelled')}
                </Notification>
            )}
            <div
                className={clsx(
                    'flex gap-4 my-4 w-full',
                    isZipFile ? 'flex-col' : 'flex-row',
                )}
            >
                {isZipFile ? (
                    <div className="mx-auto my-4">
                        <ZipFileLarge className="mx-auto w-[10rem] h-[10rem]" />
                    </div>
                ) : (
                    <div className="w-[40rem] h-[55.5rem]">
                        <PDFReader file={pdfResponse} />
                    </div>
                )}
                <div
                    className={clsx(
                        'flex-1 flex flex-col gap-y-3',
                        isZipFile ? 'mx-auto w-[30rem]' : 'max-w-[30rem]',
                    )}
                >
                    <InfoPanel isZipFile={isZipFile} />
                    {moneyRequest && (
                        <PaymentPanel moneyRequest={moneyRequest} />
                    )}
                    {payWithBizpay && (
                        <BizpayPanel bizpayAffiliateUrl={bizpayAffiliateUrl} />
                    )}
                    <AcceptOrderPanel />
                    {!isZipFile && <ReceiveDocumentPanel />}
                    <LoginMakeLeaps />
                </div>
            </div>
        </PortalPageLayout>
    );
};

export default ClientInboxItem;
